/**
 * DataFoundationRadial — central RealityOS Core octagonal hub surrounded by 8
 * radially-positioned HTML labels connected by SVG dotted spokes with gold
 * pulse nodes. Extracted from PlatformPage.jsx to keep that page under the
 * 700-line guideline.
 *
 * Props:
 *   labels: Array<{ icon, title, desc, angle }>
 *
 * Note: The HTML radial labels are hidden on mobile (they clip on narrow
 * viewports). Render `DataFoundationRadialMobile` alongside for a 2-col
 * stacked grid on small screens.
 */
export default function DataFoundationRadial({ labels }) {
  return (
    <div className="relative w-full aspect-square max-w-[680px] mx-auto" aria-hidden>
      {/* Ambient sapphire bloom */}
      <div
        className="absolute inset-[-8%] pointer-events-none"
        style={{
          background: "radial-gradient(closest-side, rgba(37,99,235,0.35), transparent 70%)",
          filter: "blur(56px)",
        }}
      />

      {/* SVG: dotted spokes to label positions + gold pulse nodes */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" fill="none">
        {labels.map((l, i) => {
          const a = (l.angle * Math.PI) / 180;
          const inner = 27, outer = 39;
          return (
            <g key={i}>
              <line
                x1={50 + inner * Math.cos(a)} y1={50 + inner * Math.sin(a)}
                x2={50 + outer * Math.cos(a)} y2={50 + outer * Math.sin(a)}
                stroke="#60A5FA" strokeOpacity="0.32" strokeWidth="0.18" strokeDasharray="0.6 0.6"
              />
              <circle
                cx={50 + outer * Math.cos(a)} cy={50 + outer * Math.sin(a)}
                r="0.85" fill="#FBBF24" opacity="0.95"
                style={{ filter: "drop-shadow(0 0 1.4px rgba(251,191,36,0.85))" }}
              />
            </g>
          );
        })}
      </svg>

      {/* Central RealityOS Core — pure SVG/CSS faceted octagonal core, structurally
          consistent with the hexagon (Autonomous) and shield (Security) elsewhere. */}
      <div
        className="absolute breathe-glow"
        style={{
          left: "25%", top: "25%", width: "50%", height: "50%",
        }}
      >
        {/* Outer ambient sapphire bloom */}
        <div
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{
            background: "radial-gradient(closest-side, rgba(96,165,250,0.32), rgba(96,165,250,0.08) 55%, transparent 90%)",
            filter: "blur(14px)",
          }}
        />

        {/* Faceted octagonal core (SVG) */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" fill="none" style={{ filter: "drop-shadow(0 0 14px rgba(96,165,250,0.55))" }}>
          <defs>
            <radialGradient id="hubFill" cx="50%" cy="50%" r="55%">
              <stop offset="0%" stopColor="#142036" stopOpacity="1" />
              <stop offset="60%" stopColor="#0B1322" stopOpacity="1" />
              <stop offset="100%" stopColor="#0A0F1A" stopOpacity="1" />
            </radialGradient>
            <linearGradient id="hubStroke" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#BFDBFE" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#60A5FA" stopOpacity="1" />
              <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0.95" />
            </linearGradient>
            <linearGradient id="hubStrokeInner" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#2563EB" stopOpacity="0.4" />
            </linearGradient>
          </defs>

          {/* Outer concentric guide ring */}
          <circle cx="50" cy="50" r="47" fill="none" stroke="#60A5FA" strokeOpacity="0.10" strokeWidth="0.3" strokeDasharray="1.4 1.4" />

          {/* Outer faceted octagon */}
          <polygon
            points="50,8 79.7,20.3 92,50 79.7,79.7 50,92 20.3,79.7 8,50 20.3,20.3"
            fill="url(#hubFill)"
            stroke="url(#hubStroke)"
            strokeWidth="1.1"
            strokeLinejoin="round"
          />

          {/* Inner faceted octagon (depth) */}
          <polygon
            points="50,22 69.8,30.2 78,50 69.8,69.8 50,78 30.2,69.8 22,50 30.2,30.2"
            fill="none"
            stroke="url(#hubStrokeInner)"
            strokeWidth="0.5"
            strokeLinejoin="round"
          />

          {/* Facet lines from vertices toward center — give the "3D" gem feel */}
          <g stroke="#60A5FA" strokeOpacity="0.32" strokeWidth="0.25">
            <line x1="50" y1="8" x2="50" y2="22" />
            <line x1="79.7" y1="20.3" x2="69.8" y2="30.2" />
            <line x1="92" y1="50" x2="78" y2="50" />
            <line x1="79.7" y1="79.7" x2="69.8" y2="69.8" />
            <line x1="50" y1="92" x2="50" y2="78" />
            <line x1="20.3" y1="79.7" x2="30.2" y2="69.8" />
            <line x1="8" y1="50" x2="22" y2="50" />
            <line x1="20.3" y1="20.3" x2="30.2" y2="30.2" />
          </g>

          {/* Inner sapphire vertex points */}
          <g fill="#93C5FD">
            <circle cx="50" cy="22" r="0.9" />
            <circle cx="69.8" cy="30.2" r="0.9" />
            <circle cx="78" cy="50" r="0.9" />
            <circle cx="69.8" cy="69.8" r="0.9" />
            <circle cx="50" cy="78" r="0.9" />
            <circle cx="30.2" cy="69.8" r="0.9" />
            <circle cx="22" cy="50" r="0.9" />
            <circle cx="30.2" cy="30.2" r="0.9" />
          </g>
        </svg>

        {/* Inner core text — crisp HTML, centered */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="font-display text-platinum text-[13px] sm:text-[16px] lg:text-[18px] tracking-tight leading-[1.05]">RealityOS</div>
            <div className="font-display text-platinum text-[13px] sm:text-[16px] lg:text-[18px] tracking-tight leading-[1.05]">Core</div>
            <div className="mt-1.5 mx-auto w-6 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(251,191,36,0.85), transparent)", boxShadow: "0 0 6px rgba(251,191,36,0.85)" }} />
          </div>
        </div>
      </div>

      {/* HTML radial labels — hidden on mobile (they clip on narrow screens); a stacked grid renders below instead */}
      {labels.map((l, i) => {
        const a = (l.angle * Math.PI) / 180;
        const R = 47; // % radius for label center
        const x = 50 + R * Math.cos(a);
        const y = 50 + R * Math.sin(a);
        const anchorRight = x < 50;
        return (
          <div
            key={i}
            className="hidden sm:block absolute z-10 max-w-[150px] sm:max-w-[170px]"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(${anchorRight ? "-100%" : "0"}, -50%)`,
              textAlign: anchorRight ? "right" : "left",
            }}
          >
            <div className={`flex items-center gap-1.5 ${anchorRight ? "justify-end" : "justify-start"}`}>
              <l.icon className="w-3.5 h-3.5 text-sapphire-glow shrink-0" />
              <div className="font-display text-platinum text-[12px] sm:text-[13px] tracking-tight leading-tight">{l.title}</div>
            </div>
            <div className="mt-1 text-[10.5px] sm:text-[11px] text-platinum/55 leading-snug">{l.desc}</div>
          </div>
        );
      })}
    </div>
  );
}
